<template>
  <section
    id="acces"
    class="access"
  >
    <!-- Texte posé sur la mer (carte vectorielle prolongée à droite pour lui
         laisser la place) ; distances au survol des cartouches. -->
    <div class="access__map">
      <img
        class="access__map-img is-desktop"
        :src="data.access.map"
        alt="Carte des accès - Templiers Valley entre Nice, Antibes et Cannes"
      >
      <img
        class="access__map-img is-mobile"
        :src="data.access.mapMobile"
        alt="Carte des accès - Templiers Valley"
      >

      <button
        v-for="pin in data.access.pins"
        :key="pin.id"
        class="access__pin"
        :style="{ left: pin.x + '%', top: pin.y + '%' }"
        :aria-label="pin.distance"
      >
        <span class="access__pin-dot" />
        <span class="access__pin-chip">
          <b>&gt;</b> {{ pin.distance }}
        </span>
      </button>

      <div class="access__panel">
        <p class="section-kicker access__kicker">{{ data.access.kicker }}</p>
        <h2 class="section-title">{{ data.access.title }}</h2>
        <p
          v-for="(p, i) in bodyParagraphs"
          :key="i"
          class="access__paragraph"
          v-html="p"
        />
      </div>
    </div>

    <!-- Mobile : le texte passe sous la carte (maquette mobile) -->
    <div class="access__text-mobile">
      <p class="section-kicker access__kicker">{{ data.access.kicker }}</p>
      <h2 class="section-title">{{ data.access.title }}</h2>
      <p
        v-for="(p, i) in bodyParagraphs"
        :key="i"
        class="access__paragraph"
        v-html="p"
      />
    </div>
  </section>
</template>

<script>
import { toParagraphs } from '@/content/richtext'

export default {
  name: 'AccessSection',
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  computed: {
    bodyParagraphs() {
      return toParagraphs(this.data.access.body)
    }
  }
}
</script>

<style lang="scss" scoped>
.access {
  background: $navy;
  display: flex;
  flex-direction: column;
}

.access__map {
  position: relative;
}

.access__map-img {
  width: 100%;
  display: block;

  &.is-mobile {
    display: none;
  }
}

.access__panel {
  position: absolute;
  top: 56%;
  transform: translateY(-50%);
  right: clamp(70px, 11vw, 200px);
  width: min(480px, 40%);
  color: $white;
}

.access__kicker {
  color: $yellow;
}

.access__paragraph {
  margin-top: 16px;
  font-size: 14.5px;
  line-height: 1.65;

  :deep(strong) {
    font-weight: 800;
  }
}

// pastilles interactives - la distance apparaît au survol.
// Tailles en vw : la carte occupe toute la largeur, les cercles suivent donc
// son échelle exactement comme sur la maquette.
.access__pin {
  --dot: 0.905vw;
  --gap: 0.333vw;
  --stroke: 0.121vw;
  --halo: 3.14vw;

  position: absolute;
  transform: translate(-50%, -50%);
  background: none;
  border: none;
  // zone de survol large, indépendante de la taille du cercle
  padding: max(24px, 1.9vw);
  z-index: 3;

  // cercle blanc de la maquette : point plein + anneau fin (box-shadow),
  // halo diffus dessiné à part pour ne pas noyer l'anneau
  &-dot {
    position: relative;
    display: block;
    width: var(--dot);
    height: var(--dot);
    min-width: 9px;
    min-height: 9px;
    border-radius: 50%;
    background: $white;
    transition: transform 0.22s ease;

    // anneau fin, détaché du point par un espace
    &::after {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      box-sizing: border-box;
      width: calc(var(--dot) + 2 * (var(--gap) + var(--stroke)));
      height: calc(var(--dot) + 2 * (var(--gap) + var(--stroke)));
      transform: translate(-50%, -50%);
      border: var(--stroke) solid rgba(255, 255, 255, 0.92);
      border-radius: 50%;
    }

    &::before {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      z-index: -1;
      width: var(--halo);
      height: var(--halo);
      transform: translate(-50%, -50%);
      border-radius: 50%;
      // halo large et plat puis chute nette, comme sur la maquette :
      // il doit rester plus discret que l'anneau pour ne pas le noyer
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.46) 0 72%,
        rgba(255, 255, 255, 0) 100%
      );
      transition: opacity 0.22s ease, transform 0.22s ease;
    }
  }

  // bulle à droite du point
  &-chip {
    position: absolute;
    left: calc(100% + 6px);
    top: 50%;
    transform: translateY(-50%) translateX(-6px);
    white-space: nowrap;
    background: $white;
    color: $navy;
    font-size: 16px;
    font-weight: 700;
    border-radius: 999px;
    padding: 10px 20px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.22s ease, transform 0.22s ease;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22);

    &::after {
      content: '';
      position: absolute;
      right: 100%;
      top: 50%;
      transform: translateY(-50%);
      border: 7px solid transparent;
      border-right-color: $white;
    }

    b {
      color: $red;
      margin-right: 5px;
    }
  }

  &:hover,
  &:focus-visible {
    .access__pin-chip {
      opacity: 1;
      transform: translateY(-50%) translateX(0);
    }

    .access__pin-dot {
      transform: scale(1.2);

      &::before {
        transform: translate(-50%, -50%) scale(1.25);
      }
    }
  }
}

.access__text-mobile {
  display: none;
}

@include mobile {
  .access__map-img {
    &.is-desktop { display: none; }
    &.is-mobile { display: block; }
  }

  .access__panel {
    display: none;
  }

  .access {
    flex-direction: column-reverse;
  }

  // maquette mobile : kicker rouge, titre/texte navy sur fond crème
  .access__text-mobile {
    display: block;
    background: $beige;
    color: $navy;
    padding: 56px 22px 40px;

    .access__kicker {
      color: $red;
    }
  }

  // le survol n'existe pas au toucher : pas d'épingles en mobile
  .access__pin {
    display: none;
  }
}
</style>
